import ContractingFooter from "features/contracting/components/ContractingFooter";
import ContractingStepFormProps from "features/contracting/models/ContractingStepFormProps";
import { useContractingStep } from "features/contracting/hooks/useContractingStep";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import ContractingState from "infrastructure/store/contracting/ContractingState";
import { useForm } from "features/shared/forms/hooks/useForm";
import PageCard from "features/shared/components/card/PageCard";
import AddressSubForm from "features/contracting/components/address/AddressSubForm";
import PlaceOfDeliveryDataSubForm from "features/contracting/components/PlaceOfDeliveryDataSubForm";
import DistributionRateTypeEnumDTO from "features/shared/enums/dynamic/DistributionRateTypeEnumDTO";
import { placeOfDeliveryFormValidations } from "./placeOfDeliveryFormValidation";
import RadioBoxBoolSubForm from "features/shared/forms/components/radioBox/RadioBoxBoolSubForm";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import { getFormInitValues } from "./placeOfDeliveryFormHelper";
import { setAddressValidations, setCadastralInfoValidations, setEvidenceNumberValidation } from "features/contracting/components/address/addressSubFormValidation";
import { useContractingAddressName } from "features/contracting/hooks/useContractingAddressName";
import { useContractingCustomerTypeValue } from "features/contracting/hooks/useContractingCustomerTypeValue";
import { Commodity } from "features/shared/enums/commodity";
import { UserEnumDTO } from "features/shared/enums/dynamic/UserEnumDTO";
import { useAfterInit } from "features/shared/hooks/useAfterInit";
import { getFormAddressKeys } from "features/contracting/components/address/addressSubFormHelper";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";

// constants
const placeOfDeliveryAddressKey = "placeOfDeliveryAddress";

// components
const PlaceOfDeliveryFormSection = (props: ContractingStepFormProps) => {
  //translations
  const tLabels = useTranslationWithPrefix("contracting\\contracting", "placeOfDeliveryFormSection");

  // store
  const stepData = useSelector<IAppState, ContractingState>(
    (store) => store.contracting
  ).progress.steps[props.actualStep.component];

  const configuration = useSelector<IAppState, ContractingState>(
    (store) => store.contracting
  ).configuration;

  const commodity = useContractingCustomerTypeValue<Commodity>("commodity");
  const category = useContractingCustomerTypeValue<string>(
    "contractCategoryCode"
  );
  const addressName = useContractingAddressName();

  const consumptionCharacteristics: UserEnumDTO[] =
    configuration.consumptionCharacteristics!;

  const distributionRates: DistributionRateTypeEnumDTO[] =
    configuration.distributionTariffs!.filter((x) => x.category === category);

  // form hook
  const formHandler = useForm({
    initialValues: getFormInitValues(stepData.values),
    initialValidations: placeOfDeliveryFormValidations(commodity),
  });

  const {
    values,
    valideteForm,
    getFormValue,
    clearFormValues,
    setValidationRule
  } = { ...formHandler };

  // form-step hook
  const { onPrevClick, onSaveClick, onNextClick } = useContractingStep({
    ...props,
    formValues: values,
    validateForm: valideteForm,
  });

  // after init hook
  const afterInit = useAfterInit();

  // side-effects
  // handling validation/clearing of custom place of delivery address
  useEffect(() => {
    if (hasCustomAddress()) {
      setAddressValidations(placeOfDeliveryAddressKey, setValidationRule);
    } else {
      setAddressValidations(
        placeOfDeliveryAddressKey,
        setValidationRule,
        "remove"
      );
      clearFormValues([placeOfDeliveryAddressKey]);
    }
  }, [getFormValue("placeOfDeliveryAddressIsSameAsMain")]);

  // handling validation/clearing of  place of delivery address - cadastral info vs standard address
  useEffect(() => {
    // validations switching (main address or cadastral info)
    if (hasCadastralInfo()) {
      setCadastralInfoValidations(placeOfDeliveryAddressKey, setValidationRule);
      setEvidenceNumberValidation(placeOfDeliveryAddressKey, setValidationRule, "remove");
    } else {
      setCadastralInfoValidations(placeOfDeliveryAddressKey, setValidationRule, "remove");
      // set validation only with custom place of delviery address
      if (hasCustomAddress()) {
        setEvidenceNumberValidation(placeOfDeliveryAddressKey, setValidationRule);   
      }
    }
    // on switch "hasCadastralInfo" clear filled values
    if (afterInit) {
      clearFormValues(getFormAddressKeys(placeOfDeliveryAddressKey));
    }
  }, [getFormValue("placeOfDeliveryAddressHasCadastralInfo")]);

  // helpers
  const hasCustomAddress = () => {
    const addressIsSameAsMain = getFormValue<boolean>(
      "placeOfDeliveryAddressIsSameAsMain"
    );
    return addressIsSameAsMain === false;
  };

  const hasCadastralInfo = () => {
    const hasCadastralInfo = getFormValue("placeOfDeliveryAddressHasCadastralInfo");
    return hasCadastralInfo === true;
  };

  // render
  return (
    <>
      <PlaceOfDeliveryDataSubForm
        formHandler={formHandler}
        commodity={commodity}
        distributionRates={distributionRates}
        consumptionCharacteristics={consumptionCharacteristics}
      />
      <PageCard
        title={tLabels("addressCardTitle")}
        bodyClassName="pt-4 px-4"
        cardClassName="mb-4"
      >
        <div className="row mx-n2">
          <div className="col-md-6 px-2">
            <RadioBoxBoolSubForm
              name={"placeOfDeliveryAddressIsSameAsMain"}
              label={`${tLabels("placeOfDeliveryAddressIsSameAsMain")} ${addressName.toLocaleLowerCase()}?`}
              formHandler={formHandler}
            />
          </div>
          <RenderWrapper isVisible={hasCustomAddress}>
            <div className="col-md-6 px-2">
              <RadioBoxBoolSubForm
                name={"placeOfDeliveryAddressHasCadastralInfo"}
                label={tLabels("placeOfDeliveryAddressHasCadastralInfo")}
                formHandler={formHandler}
              />
            </div>
          </RenderWrapper>
        </div>
        <RenderWrapper isVisible={hasCustomAddress}>
          <AddressSubForm
            formHandler={formHandler}
            objectPrefix={placeOfDeliveryAddressKey}
            enableAutocomplete={configuration.enableAddressAutocomplete}
            hasCadastralInfo={hasCadastralInfo()}
          />
        </RenderWrapper>
      </PageCard>
      <ContractingFooter
        onPrevClick={onPrevClick}
        onNextClick={onNextClick}
        onSaveClick={props.enableSaveBtn ? onSaveClick : undefined}
      />
    </>
  );
};

export default PlaceOfDeliveryFormSection;
